import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";

const COLORS = {
  primary: "#3AAFA9",
  secondary: "#7BC8A4",
  heading: "#2E2E2E",
  text: "#4B5563",
  sectionTitle: "#1F7A75",
  background: "#3AAFA9",
  cardBg: "#F9FAFB",
  border: "#E5E7EB",
};

const deletionSteps = [
  {
    title: "1. In-App Account Deletion (Recommended)",
    text: "You can effortlessly request permanent account deletion directly within the Janak mobile app on iOS and Android:",
    list: [
      "Open the Janak App on your iOS or Android device and log in.",
      "Navigate to your Profile screen or tap on Settings.",
      "Scroll to the bottom and select the 'Delete Account' option.",
      "Confirm your decision on the confirmation dialog. Upon verification, your session will terminate immediately and data removal protocols will begin."
    ]
  },
  {
    title: "2. Web-Based Deletion Request (Without Mobile App)",
    text: "If you no longer have access to your mobile device or have already reinstalled/uninstalled the app, you can initiate data deletion online:",
    list: [
      "Send an email directly to our Data Privacy Officer at contact@thejanak.com or care@thejanak.com.",
      "Use the Subject Line: 'Account Deletion Request - [Your Registered Mobile Number / Email]'.",
      "Include your registered full name and phone number in the body of the email for identity verification.",
      "Our support team will process your verification within 48 hours and complete data erasure within 7 to 14 business days."
    ]
  },
  {
    title: "3. What Data Is Deleted",
    text: "When an account deletion request is finalized, the following personal identifiers and application records are securely purged:",
    list: [
      "Personal Identifiers: Your full name, registered mobile number, profile picture, and primary address.",
      "Care Coordination Records: Saved elder profile details, preferences, and non-statutory service interaction notes.",
      "Location Logs: Past addresses, geographic navigation tracks, and emergency SOS alert routing histories.",
      "Device Metadata: Firebase device registration tokens, notification IDs, and diagnostic app crash logs."
    ]
  },
  {
    title: "4. Data Retention Exceptions",
    text: "Under applicable Indian regulatory frameworks, including the Information Technology Act, 2000 and financial audit laws, certain records must be preserved temporarily after account deletion:",
    list: [
      "Financial Transaction & Billing History: Records of completed transactions via payment gateways (such as Cashfree) are archived for tax compliance and accounting regulations for a period of up to 5 years.",
      "Legal Dispute & Safety Investigations: Any active service dispute, grievance log, or regulatory compliance record may be retained until fully resolved before permanent anonymization."
    ]
  },
  {
    title: "5. Contact & Grievance Assistance",
    text: "For immediate assistance regarding data rights, withdrawal of consent, or account deletion inquiries, please reach out to our dedicated support desks:",
    list: [
      "Email Support: contact@thejanak.com / care@thejanak.com",
      "Helpline Phone: +91 90981 14568 (Operating 9:00 AM – 7:00 PM IST)",
      "Mailing Address: Janak Elderbridge Private Limited, India."
    ]
  }
];

const AccountDeletion = () => {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const headingRef = useRef(null);
  const introRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", reason: "" });

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
      tl.from(sectionRef.current, { opacity: 0, duration: 0.5 })
        .from(cardRef.current, { y: 36, opacity: 0, duration: 0.7 }, "-=0.2")
        .from(headingRef.current, { y: 18, opacity: 0, duration: 0.45 }, "-=0.2")
        .from(introRef.current, { y: 16, opacity: 0, duration: 0.45 }, "-=0.25")
        .from(
          ".deletion-section-item",
          { y: 24, opacity: 0, stagger: 0.07, duration: 0.38 },
          "-=0.2",
        );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.phone) return;
    
    const subject = encodeURIComponent(`Account Deletion Request - ${formData.phone}`);
    const body = encodeURIComponent(
      `Hello Data Privacy Officer,\n\nI would like to request the permanent deletion of my account and associated data.\n\nDetails:\nName: ${formData.name}\nRegistered Mobile/Email: ${formData.phone}\nReason: ${formData.reason || "Not provided"}\n\nPlease process this request and confirm once my data is deleted.\n\nThank you.`
    );
    
    window.location.href = `mailto:care@thejanak.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen overflow-hidden px-6 py-20"
      style={{ background: COLORS.background }}
    >
      <div
        className="absolute left-10 top-24 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.primary}33` }}
      />
      <div
        className="absolute bottom-20 right-10 h-72 w-72 rounded-full blur-3xl"
        style={{ background: `${COLORS.secondary}40` }}
      />

      <div
        ref={cardRef}
        className="relative mx-auto max-w-4xl rounded-3xl bg-white/90 px-6 py-12 text-gray-800 shadow-[0_30px_90px_rgba(0,0,0,0.18)] backdrop-blur-2xl md:px-12"
      >
        <h1
          ref={headingRef}
          className="mb-6 text-center text-4xl font-extrabold md:text-5xl"
          style={{ color: COLORS.heading }}
        >
          Account Deletion & Data Erasure
        </h1>

        <p
          ref={introRef}
          className="mb-12 text-center text-base leading-relaxed md:text-lg"
          style={{ color: COLORS.text }}
        >
          In full compliance with Google Play Store, Apple App Store policies, and the Digital Personal Data Protection Act (DPDP Act, 2023),{" "}
          <span className="font-semibold" style={{ color: COLORS.primary }}>
            Janak
          </span>{" "}
          provides transparent, simple pathways to permanently delete your account and personal data.
        </p>

        {/* Quick Deletion Form Box for Web Compliance */}
        <div className="mb-12 rounded-2xl border p-6 md:p-8" style={{ backgroundColor: COLORS.cardBg, borderColor: COLORS.border }}>
          <h3 className="mb-3 text-xl font-bold" style={{ color: COLORS.sectionTitle }}>
            🚀 Online Account Deletion Request
          </h3>
          <p className="mb-6 text-sm leading-relaxed" style={{ color: COLORS.text }}>
            Need to request deletion online without logging into the app? Fill out this verification form to initiate secure account deletion with our privacy support team.
          </p>
          {submitted ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-800 text-center font-medium">
              ✅ Your account deletion request has been registered. Our Data Protection Team will review and verify your identity via email or SMS within 48 hours before completing permanent account removal.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Registered Full Name</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Rahul Sharma"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full rounded-xl border px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3AAFA9]"
                    style={{ borderColor: COLORS.border }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Registered Mobile Number / Email *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. +91 9876543210 or care@janak.com"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full rounded-xl border px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3AAFA9]"
                    style={{ borderColor: COLORS.border }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">Reason for Deletion (Optional)</label>
                <textarea
                  rows={2}
                  placeholder="Tell us how we can improve or why you are leaving..."
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full rounded-xl border px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#3AAFA9]"
                  style={{ borderColor: COLORS.border }}
                />
              </div>
              <button
                type="submit"
                className="w-full md:w-auto px-8 py-3 rounded-xl text-white font-semibold text-sm transition shadow-lg hover:opacity-90"
                style={{ backgroundColor: COLORS.primary }}
              >
                Submit Deletion Request
              </button>
            </form>
          )}
        </div>

        <div className="space-y-10 text-[15.5px] leading-relaxed">
          {deletionSteps.map((section) => (
            <div key={section.title} className="deletion-section-item">
              <h2
                className="mb-3 text-xl font-bold md:text-2xl"
                style={{ color: COLORS.sectionTitle }}
              >
                {section.title}
              </h2>
              {section.text ? <p className="mb-2" style={{ color: COLORS.text }}>{section.text}</p> : null}
              {section.list ? (
                <ul className="list-disc space-y-2 pl-6" style={{ color: COLORS.text }}>
                  {section.list.map((item, index) => (
                    <li key={index} className="leading-normal">{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
        </div>

        <p className="mt-16 text-center text-sm text-gray-500">Last updated: July 2026</p>
      </div>
    </section>
  );
};

export default AccountDeletion;
